import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUniversity,
  faMoneyCheckDollar,
  faBuilding,
  faCheckCircle,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import {
  AfterSalePurchaseStyles,
  OfferCard,
  OfferIconBadge,
  OfferAmountBox,
  OfferAmountLabel,
  OfferAmountValue,
  OfferAcceptButton,
  OfferRejectButton,
  OfferStatusBadge,
  OfferStatusIcon,
  BankDetailsCard,
  BankDetailsIcon,
  BankDetailsGrid,
  BankDetailsLabel,
  BankDetailsValue,
  SubmitBankButton,
} from '../../common/AfterSale/AfterSaleCreate.styles';
import AfterSaleInfoBox from '../../common/AfterSale/AfterSaleInfoBox';
import {
  UploadDocumentsContainer,
  UploadWrapper,
} from '../purchase/Purchase.styles';
import AfterSaleReplacementDocuments from '../../common/AfterSale/AfterSaleReplacementDocuments';
import {
  getAfterSalePurchase,
  resetAfterSalePurchase,
} from '../../features/afterSalePurchase/afterSalePurchaseActions';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
import AfterSaleSignatureTypeDocuments from '../../common/AfterSale/AfterSaleSignatureTypeDocuments';
import { getDocumentTypes } from '../../features/afterSale/afterSaleActions';
import PaymentStatusAfterSale from '../../common/components/redesignComponents/Purchase/PaymentStatusAfterSale';
import AccidentsUploader from '../../common/components/redesignComponents/Uploader/AccidentsUploader';
import {
  generalAlertError,
  generalAlertSuccess,
  confirmAlert,
} from '../../common/utils/alerts';
import managementAxe from '../../services/axios-client/managementServisAxeClient';
import { FormInputText } from '../../common/formInputs/FormInputText';
import { QuoteInfoContainerAfterSale } from '../../common/components/redesignComponents/Purchase/PaymentStatusAfterSale.styles';
import { apiDownloadDocument } from '../../services/axios-client/axeCommons';
import {
  ActionsWrapper,
  DocumentFileActions,
  DocumentInfo,
  DocumentInfoTitle,
  FileInfo,
  MainTitle,
} from '../../common/components/redesignComponents/Purchase/SignatureType.styles';
// import { QuoteInfoContainerAfterSale, WaitingForPaymentQuoteInfoAfterSale } from '../../common/components/redesignComponents/Purchase/PaymentStatusAfterSale.styles';
// import { addAccidentNote } from '../../features/accidents/accidentsActions';
import fileIcon from '../../assets/redesign_icons/file.svg';
import downloadIcon from '../../assets/redesign_icons/download.svg';
import ConfirmationModal from '../../common/components/modals/ConfirmationModal';

const bankDetailsSchema = yup.object({
  iban: yup
    .string()
    .required('IBAN è obbligatorio')
    .matches(/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/, 'IBAN non valido'),
  swift_code: yup
    .string()
    .required('SWIFT Code è obbligatorio')
    .matches(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, 'SWIFT Code non valido'),
  bank_name: yup
    .string()
    .required('Nome Banca è obbligatorio')
    .min(2, 'Il nome della banca deve contenere almeno 2 caratteri'),
});

// const schema = yup.object({
//   note: yup.string().required('Questo campo e obligatorio'),
// });

function AfterSalePurchase() {
  const { afterSaleId, afterSaleType } = useParams();
  const { document_types } = useSelector((store) => store.afterSale);
  const { loading, afterSale, documents } = useSelector(
    (store) => store.afterSalePurchase.after_sale_purchase,
  );
  const navigate = useNavigate();
  const [submittingBankDetails, setSubmittingBankDetails] = useState(false);
  const [confirmingCompletion, setConfirmingCompletion] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [respondingToOffer, setRespondingToOffer] = useState(false);

  const {
    register: registerBank,
    formState: { errors: errorsBank },
    handleSubmit: handleSubmitBank,
    reset: resetBank,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(bankDetailsSchema),
    defaultValues: {
      iban: '',
      swift_code: '',
      bank_name: '',
    },
  });

  // const {
  //   register, formState: { errors }, handleSubmit, reset,
  // } = useForm({
  //   mode: 'all',
  //   resolver: yupResolver(schema),
  //   defaultValues: {
  //     note: '',
  //   },
  // });

  const collectData = async () => {
    getAfterSalePurchase(afterSaleId, afterSaleType);
  };

  // const addNote = (data) => {
  //   addAccidentNote(afterSaleId, data);
  //   reset({ note: '' });
  //   collectData();
  // };

  const uploadAccidentDocuments = async (accidentId, data) => {
    const response = await managementAxe.post(
      `/broker/after-sale/accidents/documents/${accidentId}`,
      { files: data },
    );
    if (response.status !== 200) {
      generalAlertError('I File non sono stati caricati. Riprova!');
      return;
    }

    generalAlertSuccess('I file sono stati caricati con successo!');
    collectData();
  };

  const submitBankDetails = async (data) => {
    setSubmittingBankDetails(true);
    try {
      const response = await managementAxe.put(
        `/broker/after-sale/accidents/${afterSaleId}/bank-details`,
        data,
      );

      if (response.status === 200) {
        generalAlertSuccess('Dati bancari inviati con successo!');
        resetBank();
        collectData();
      } else {
        generalAlertError("Errore durante l'invio dei dati bancari. Riprova!");
      }
    } catch (error) {
      generalAlertError("Errore durante l'invio dei dati bancari. Riprova!");
    } finally {
      setSubmittingBankDetails(false);
    }
  };

  const confirmMoneyReceived = async () => {
    setConfirmingCompletion(true);
    try {
      const response = await managementAxe.put(
        `/broker/after-sale/accidents/${afterSaleId}/confirm-completion`,
      );

      if (response.status === 200) {
        generalAlertSuccess(
          'Confermato! Il caso è stato completato con successo!',
        );
        setShowConfirmModal(false);
        collectData();
      } else {
        generalAlertError('Errore durante la conferma. Riprova!');
      }
    } catch (error) {
      generalAlertError('Errore durante la conferma. Riprova!');
    } finally {
      setConfirmingCompletion(false);
    }
  };

  const respondToOffer = async (accepted) => {
    setRespondingToOffer(true);
    try {
      const response = await managementAxe.get(
        `/broker/after-sale/accidents/${afterSaleId}/offer-response/${accepted === true ? '1' : '0'}`,
      );

      if (response.status === 200) {
        generalAlertSuccess(
          accepted
            ? 'Offerta accettata con successo!'
            : 'Offerta rifiutata con successo!',
        );
        collectData();
      } else {
        generalAlertError('Errore durante la risposta. Riprova!');
      }
    } catch (error) {
      generalAlertError('Errore durante la risposta. Riprova!');
    } finally {
      setRespondingToOffer(false);
    }
  };

  const handleOpenModal = () => {
    setShowConfirmModal(true);
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
  };

  useEffect(() => {
    collectData();
    return () => {
      resetAfterSalePurchase();
    };
  }, []);

  useEffect(() => {
    getDocumentTypes(afterSaleType);
  }, [afterSaleType]);

  // Populate form with existing bank details if they exist
  useEffect(() => {
    if (
      afterSale
      && afterSale.iban
      && afterSale.swift_code
      && afterSale.bank_name
    ) {
      resetBank({
        iban: afterSale.iban || '',
        swift_code: afterSale.swift_code || '',
        bank_name: afterSale.bank_name || '',
      });
    }
  }, [afterSale, resetBank]);

  if (loading) {
    return <PageLoading />;
  }

  const getPostVenditaName = (type) => {
    if (type === 'replacement') {
      return 'per la sostituzione';
    }

    if (type === 'suspension') {
      return 'per la sospensione';
    }

    if (type === 'cancellation') {
      return 'per la cessazione';
    }

    if (type === 'modification') {
      return 'per modifica dati';
    }

    if (type === 'accident') {
      return 'per il sinistro (massimo 10 file)';
    }

    return '';
  };

  const getPostVenditaNameSign = (type) => {
    if (type === 'replacement') {
      return 'per la sostituzione';
    }

    if (type === 'suspension') {
      return 'per la sospensione';
    }

    if (type === 'cancellation') {
      return 'per la cessazione';
    }

    if (type === 'modification') {
      return 'per modifica dati';
    }

    if (type === 'accident') {
      return 'per il sinistro';
    }

    return '';
  };

  const navigateToFinalPage = (type) => {
    if (type === 'replacement') {
      navigate(`/replacements/${afterSale.id}`);
    }
    if (type === 'modification') {
      navigate(`/modifications/${afterSale.id}`);
    }
    if (type === 'cancellation') {
      navigate(`/cancellations/${afterSale.id}`);
    }
    if (type === 'accident') {
      navigate(`/accidents/${afterSale.id}`);
    }
    if (type === 'suspension') {
      navigate(`/suspensions/${afterSale.id}`);
    }
  };

  const downloadDocument = async (documentId) => {
    const response = await apiDownloadDocument(documentId);
    const alink = document.createElement('a');
    alink.target = '_blank';
    alink.href = response.data.data.contents;
    alink.download = response.data.data.filename;
    alink.click();
  };

  if (afterSale) {
    const status = afterSale.status ?? afterSale.status_id;

    if ((afterSaleType === 'accident' && status === 4) || status === 5) {
      navigateToFinalPage(afterSaleType);
    }
  }

  return (
    <AfterSalePurchaseStyles>
      <AfterSaleInfoBox afterSale={afterSale} afterSaleType={afterSaleType} />

      {/* Uploaded Accident Files Section */}
      {afterSale.accident_files && afterSale.accident_files.length > 0 && (
        <div className="mt-4 mb-4">
          <div className="d-flex align-items-center justify-content-start mb-3">
            <MainTitle>
              Documenti caricati
              {' '}
              <b>{getPostVenditaNameSign(afterSaleType)}</b>
            </MainTitle>
          </div>

          <div className="row g-3">
            {afterSale.accident_files.map((file, index) => (
              <div
                key={index}
                className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-3"
              >
                <DocumentInfo style={{ height: '100%' }}>
                  <DocumentInfoTitle>
                    <FileInfo className="w-100">
                      <img
                        src={fileIcon}
                        alt={file.document.original_filename}
                        width={20}
                        style={{ flexShrink: 0 }}
                      />
                      <p
                        className="mb-0 text-truncate flex-grow-1"
                        title={file.document.original_filename}
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {file.document.original_filename}
                      </p>
                    </FileInfo>
                  </DocumentInfoTitle>
                  <DocumentFileActions className="w-100">
                    <ActionsWrapper>
                      <div
                        className="action-button"
                        onClick={() => downloadDocument(file.document_id)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') downloadDocument(file.document_id);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <img src={downloadIcon} alt="Scarica" width={30} />
                        <p className="mb-0">Scarica</p>
                      </div>
                    </ActionsWrapper>
                  </DocumentFileActions>
                </DocumentInfo>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* {afterSaleType === 'accident' && afterSale.status_id === 4 && (
        <div className="row mt-3">
          <div className="col-12">
            <WaitingForPaymentQuoteInfoAfterSale>
              <QuoteInfoContainerAfterSale>
                <div className="single-accident-two">
                  <h3 className="ps-3">
                    <strong>
                      Nota
                    </strong>
                  </h3>
                  {afterSale.notes.length
                    ? afterSale.notes.map((note, index) => (
                      <p key={index} className="mx-3 p-2 mt-3 bg-light shadow-sm">{note.note}</p>
                    ))
                    : <p>Nessuna nota trovata!</p>}

                </div>
                <form onSubmit={handleSubmit(addNote)} className="single-accident-three px-3">
                  <div className="mb-3 ">
                    <label className="form-label" htmlFor="note">Aggiungi nuova nota</label>
                    <textarea name="note" className={`form-control ${errors.note?.message ? 'is-invalid' : ''}`} rows="5" {...register('note')} />
                    <div className="invalid-feedback">
                      {errors.note?.message}
                    </div>
                    <button type="submit" className="btn btn-success px-4 mt-2">Invia</button>
                  </div>
                </form>
              </QuoteInfoContainerAfterSale>
            </WaitingForPaymentQuoteInfoAfterSale>
          </div>
        </div>
      )} */}
      {([1, 2].includes(afterSale.status)
        || [1, 2].includes(afterSale.status_id)) && (
          <div className="mt-3">
            {['cancellation', 'modification', 'replacement'].includes(
              afterSaleType,
            ) && (
            <UploadDocumentsContainer>
              <div className="d-flex align-items-center justify-content-start">
                <h2>
                  Carica i
                  {' '}
                  <b>documenti</b>
                  {' '}
                  {getPostVenditaName(afterSaleType)}
                </h2>
              </div>
              <UploadWrapper>
                <AfterSaleReplacementDocuments
                  afterSaleType={afterSaleType}
                  afterSale={afterSale}
                  document_types={document_types}
                  collectQuoteData={collectData}
                  documents={documents}
                />
              </UploadWrapper>
            </UploadDocumentsContainer>
            )}
            {afterSaleType === 'accident' && (
              <>
                {!afterSale.by_backoffice ? (
                  <UploadDocumentsContainer>
                    {/* If 10 files are uploaded hide this component */}
                    {afterSale.accident_files.length < 10 && (
                      <>
                        <div className="d-flex align-items-center justify-content-start">
                          <h2 className="mb-3">
                            Carica i
                            {' '}
                            <b>documenti</b>
                            {' '}
                            {getPostVenditaName(afterSaleType)}
                          </h2>
                        </div>
                        <div>
                          <AccidentsUploader
                            uploadDocuments={uploadAccidentDocuments}
                            accidentId={afterSaleId}
                          />
                        </div>
                      </>
                    )}
                  </UploadDocumentsContainer>
                ) : (
                  <div className="alert alert-info" role="alert">
                    I documenti per questo sinistro verranno caricati dal nostro
                    backoffice.
                  </div>
                )}
              </>
            )}
            {documents.length > 0 && (
              <AfterSaleSignatureTypeDocuments
                getPostVenditaNameSign={() => getPostVenditaNameSign(afterSaleType)}
                afterSale={afterSale}
                documents={documents}
                collectData={collectData}
                document_types={document_types}
              />
            )}
          </div>
      )}

      {(afterSale.status === 4 || afterSale.status_id === 4) && (
        <div className="card-body mt-3 ">
          <div className="row">
            <div className="col-12 col-lg-6 offset-lg-3">
              <div className="alert alert-success py-4" role="alert">
                <h4 className="alert-heading">In Attesa Smarcatura</h4>
                <p className="mt-2 mb-0">
                  Il nostro backoffice sta verificando questo caso. Riceverai
                  una notifica dopo la verifica.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {(afterSale.status === 6 || afterSale.status_id === 6) && (
        <div className="card-body mt-3 ">
          <div className="row">
            <div className="col-12 col-lg-6 offset-lg-3">
              <div className="alert alert-success py-4" role="alert">
                <h4 className="alert-heading">Inviato in compagnia</h4>
                <p className="mt-2 mb-0">
                  La tua richiesta è stata inviata alla compagnia. Riceverai una notifica non appena la compagnia darà una risposta.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {(afterSale.status === 3 || afterSale.status_id === 3) && (
        <div className="mt-3">
          {afterSale.status === 3 && (
            <PaymentStatusAfterSale
              afterSale={afterSale}
              afterSalePaymentRequest={afterSale.after_sale_payment_requests[0]}
              collectData={collectData}
            />
          )}
        </div>
      )}

      {afterSale.status_id === 3 && (
        <div className="mt-3">
          {/* Confirmation Button - Show only when bank details are submitted and case is not completed */}
          {afterSale.iban
            && afterSale.swift_code
            && afterSale.bank_name
            && !afterSale.is_completed && (
              <QuoteInfoContainerAfterSale className="mt-3">
                <div className="row">
                  <div className="col-12">
                    <div className="alert alert-info" role="alert">
                      <h5 className="alert-heading mb-3">
                        <FontAwesomeIcon
                          icon={faMoneyCheckDollar}
                          className="me-2"
                        />
                        Conferma Ricezione Pagamento
                      </h5>
                      <p className="mb-3">
                        Una volta ricevuto il pagamento dalla controparte,
                        clicca sul pulsante qui sotto per confermare il
                        completamento del sinistro. Questo aggiornerà lo stato
                        del caso come completato.
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary px-5 py-2"
                        onClick={handleOpenModal}
                      >
                        <FontAwesomeIcon
                          icon={faMoneyCheckDollar}
                          className="me-2"
                        />
                        Conferma Ricezione Pagamento
                      </button>
                    </div>
                  </div>
                </div>
              </QuoteInfoContainerAfterSale>
          )}

          {/* Completed Status Info Box - Show when case is completed */}
          {afterSale.is_completed && (
            <QuoteInfoContainerAfterSale className="mt-3">
              <div className="row">
                <div className="col-12">
                  <div className="alert alert-success" role="alert">
                    <h4 className="alert-heading mb-3">
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      Sinistro Completato
                    </h4>
                    <p className="mb-2">
                      Questo sinistro è stato completato con successo. Il
                      pagamento è stato ricevuto e confermato.
                    </p>
                    <hr />
                    <p className="mb-0 text-muted">
                      <small>
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="me-2"
                        />
                        Tutti i passaggi sono stati completati e il caso è stato
                        chiuso.
                      </small>
                    </p>
                  </div>
                </div>
              </div>
            </QuoteInfoContainerAfterSale>
          )}

          {afterSale?.offer_accepted == null ? (
            <QuoteInfoContainerAfterSale className="mt-3">
              <OfferCard bg="#fff8e1" borderColor="#ffc107">
                <div className="text-center mb-4">
                  <OfferIconBadge bg="#ffc107">
                    <FontAwesomeIcon icon={faMoneyCheckDollar} />
                  </OfferIconBadge>
                  <h4 className="fw-bold text-warning mb-2">
                    Offerta Ricevuta
                  </h4>
                  <p className="text-muted mb-0">
                    Hai ricevuto un&apos;offerta dalla controparte
                  </p>
                </div>

                {afterSale?.amount != null && (
                  <OfferAmountBox borderColor="#ffc107" className="text-center mb-4">
                    <OfferAmountLabel>Importo Offerto</OfferAmountLabel>
                    <OfferAmountValue>
                      €
                      {Number(afterSale.amount).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </OfferAmountValue>
                  </OfferAmountBox>
                )}

                <p className="text-center text-muted mb-4">
                  Accetta o rifiuta l&apos;offerta per procedere con il sinistro.
                </p>

                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <OfferAcceptButton
                    type="button"
                    onClick={() => confirmAlert(
                      "Sei sicuro di voler accettare l'offerta? Questa azione non può essere annullata.",
                      () => respondToOffer(true),
                      () => { },
                      "Accettare l'offerta?",
                      '#00A651',
                    )}
                    disabled={respondingToOffer}
                  >
                    {respondingToOffer ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    ) : (
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    )}
                    Accetta Offerta
                  </OfferAcceptButton>
                  <OfferRejectButton
                    type="button"
                    onClick={() => confirmAlert(
                      "Sei sicuro di voler rifiutare l'offerta? Questa azione non può essere annullata.",
                      () => respondToOffer(false),
                      () => { },
                      "Rifiutare l'offerta?",
                      '#dc3545',
                    )}
                    disabled={respondingToOffer}
                  >
                    {respondingToOffer ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    ) : (
                      <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                    )}
                    Rifiuta Offerta
                  </OfferRejectButton>
                </div>
              </OfferCard>
            </QuoteInfoContainerAfterSale>
          ) : afterSale.offer_accepted == 1 ? (
            <>
              {/* Offer Accepted Badge */}
              <QuoteInfoContainerAfterSale className="mt-3">
                <OfferStatusBadge>
                  <OfferStatusIcon>
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </OfferStatusIcon>
                  <div>
                    <h6 className="fw-bold mb-0" style={{ color: 'var(--greenia-color)' }}>
                      Offerta Accettata
                    </h6>
                    {afterSale?.amount != null && (
                      <p className="mb-0 small" style={{ color: 'var(--greenia-color)' }}>
                        Importo:
                        {' '}
                        <strong>
                          €
                          {Number(afterSale.amount).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                        </strong>
                      </p>
                    )}
                  </div>
                </OfferStatusBadge>
              </QuoteInfoContainerAfterSale>

              {/* Bank Details Card */}
              <QuoteInfoContainerAfterSale className="mt-3">
                <BankDetailsCard>
                  <div className="d-flex align-items-center mb-4">
                    <BankDetailsIcon>
                      <FontAwesomeIcon icon={faUniversity} />
                    </BankDetailsIcon>
                    <div>
                      <h5 className="fw-bold mb-1">
                        {afterSale.iban && afterSale.swift_code && afterSale.bank_name
                          ? 'Dati Bancari Registrati'
                          : 'Inserisci i Dati Bancari'}
                      </h5>
                      <p className="text-muted mb-0 small">
                        {afterSale.iban && afterSale.swift_code && afterSale.bank_name
                          ? 'Puoi aggiornarli se necessario'
                          : 'Compila il modulo per ricevere il rimborso'}
                      </p>
                    </div>
                  </div>

                  {afterSale.iban && afterSale.swift_code && afterSale.bank_name ? (
                    <BankDetailsGrid className="mb-4">
                      <div className="row g-3">
                        <div className="col-12 col-md-4">
                          <BankDetailsLabel>IBAN</BankDetailsLabel>
                          <BankDetailsValue>{afterSale.iban}</BankDetailsValue>
                        </div>
                        <div className="col-12 col-md-4">
                          <BankDetailsLabel>SWIFT/BIC</BankDetailsLabel>
                          <BankDetailsValue>{afterSale.swift_code}</BankDetailsValue>
                        </div>
                        <div className="col-12 col-md-4">
                          <BankDetailsLabel>Banca</BankDetailsLabel>
                          <BankDetailsValue>{afterSale.bank_name}</BankDetailsValue>
                        </div>
                      </div>
                    </BankDetailsGrid>
                  ) : (
                    <form onSubmit={handleSubmitBank(submitBankDetails)}>
                      <div className="row">
                        <div className="col-12 col-md-6 mb-3">
                          <div className="d-flex align-items-center mb-2">
                            <FontAwesomeIcon
                              icon={faMoneyCheckDollar}
                              className="me-2"
                              style={{ color: 'var(--greenia-color)' }}
                            />
                            <label htmlFor="iban" className="form-label mb-0">IBAN</label>
                          </div>
                          <FormInputText
                            inputId="iban"
                            type="text"
                            placeholder="IT60X0542811101000000123456"
                            registration={registerBank('iban')}
                            error={errorsBank.iban?.message}
                            classNameInput="text-start"
                          />
                        </div>

                        <div className="col-12 col-md-6 mb-3">
                          <div className="d-flex align-items-center mb-2">
                            <FontAwesomeIcon
                              icon={faUniversity}
                              className="me-2"
                              style={{ color: 'var(--greenia-color)' }}
                            />
                            <label htmlFor="swift_code" className="form-label mb-0">SWIFT/BIC Code</label>
                          </div>
                          <FormInputText
                            inputId="swift_code"
                            type="text"
                            placeholder="BCITITMM"
                            registration={registerBank('swift_code')}
                            error={errorsBank.swift_code?.message}
                            classNameInput="text-start"
                          />
                        </div>

                        <div className="col-12 mb-3">
                          <div className="d-flex align-items-center mb-2">
                            <FontAwesomeIcon
                              icon={faBuilding}
                              className="me-2"
                              style={{ color: 'var(--greenia-color)' }}
                            />
                            <label htmlFor="bank_name" className="form-label mb-0">Nome Banca</label>
                          </div>
                          <FormInputText
                            inputId="bank_name"
                            type="text"
                            placeholder="Intesa Sanpaolo"
                            registration={registerBank('bank_name')}
                            error={errorsBank.bank_name?.message}
                            classNameInput="text-start"
                          />
                        </div>
                      </div>

                      <div className="d-flex justify-content-end mt-3">
                        <SubmitBankButton type="submit" disabled={submittingBankDetails}>
                          {submittingBankDetails ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                              Invio in corso...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faMoneyCheckDollar} className="me-2" />
                              {afterSale.iban && afterSale.swift_code && afterSale.bank_name
                                ? 'Aggiorna dati bancari'
                                : 'Invia dati bancari'}
                            </>
                          )}
                        </SubmitBankButton>
                      </div>
                    </form>
                  )}
                </BankDetailsCard>
              </QuoteInfoContainerAfterSale>
            </>
          ) : (
            <QuoteInfoContainerAfterSale className="mt-3">
              <OfferCard bg="#fff5f5" borderColor="#dc3545">
                <div className="text-center">
                  <OfferIconBadge bg="#dc3545">
                    <FontAwesomeIcon icon={faTimesCircle} />
                  </OfferIconBadge>
                  <h4 className="fw-bold text-danger mb-2">
                    Offerta Rifiutata
                  </h4>
                  {afterSale?.amount != null && (
                    <OfferAmountBox borderColor="#dc3545" maxWidth="300px" className="mb-3">
                      <OfferAmountLabel>Importo Offerto</OfferAmountLabel>
                      <OfferAmountValue fontSize="1.8rem" color="#dc3545" strikethrough>
                        €
                        {Number(afterSale.amount).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                      </OfferAmountValue>
                    </OfferAmountBox>
                  )}
                  <p className="text-danger mb-0">
                    L&apos;offerta è stata rifiutata. Il caso verrà gestito di conseguenza.
                  </p>
                </div>
              </OfferCard>
            </QuoteInfoContainerAfterSale>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showConfirmModal}
        onClose={handleCloseModal}
        onConfirm={confirmMoneyReceived}
        title="Conferma Ricezione Pagamento"
        message="Sei sicuro di aver ricevuto il pagamento dalla controparte?"
        description="Confermando questa azione, il sinistro verrà marcato come completato. Questa operazione non può essere annullata."
        isLoading={confirmingCompletion}
        confirmText="Sì, Conferma"
        cancelText="Annulla"
        confirmButtonClass="btn-success"
      />
    </AfterSalePurchaseStyles>
  );
}

export default AfterSalePurchase;
